// src/components/ads-admin/AdsChatDrawer.jsx
// MAD Pilot — persistent DB-backed chat sessions with parallel extraction + synthesis.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  adsCreateChatSession,
  adsGetChatSession,
  adsListChatSessions,
  adsRenameChatSession,
  adsDeleteChatSession,
  adsChatSessionStream,
  settingsList,
} from "../../services/api";
import { exportMadPilotMessage, exportMadPilotSession } from "./adsChatPptxExport";

const VISION_MODELS = new Set([
  "gpt-4o", "gpt-4o-mini", "gpt-4.1", "gpt-5", "gpt-5-mini",
  "gemini-2.5-pro", "gemini-2.5-flash",
]);

const MODELS = [
  { id: "gpt-4.1",          label: "GPT-4.1" },
  { id: "gpt-5",            label: "GPT-5" },
  { id: "gpt-5-mini",       label: "GPT-5 mini" },
  { id: "gpt-4o-mini",      label: "GPT-4o mini" },
  { id: "sonar",            label: "Perplexity Sonar" },
  { id: "sonar-pro",        label: "Perplexity Sonar Pro" },
  { id: "sonar-reasoning",  label: "Perplexity Sonar Reasoning" },
  { id: "gemini-2.5-pro",   label: "Gemini 2.5 Pro" },
  { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
];

const DEFAULT_WIDTH = 560;
const MIN_WIDTH = 360;

const COST_ALERT_KEY = "ads.chat.cost_alert_threshold";
const COST_MILESTONES = [1, 5, 10, 20, 50]; // fixed USD warning levels

function getAlertMilestones(threshold) {
  const t = Math.max(0, parseFloat(threshold) || 1.0);
  const above = COST_MILESTONES.filter(m => m > t);
  return [...new Set([t, ...above])].sort((a, b) => a - b);
}

const DEFAULT_AUTO_MESSAGE =
  "Please provide a comprehensive analysis of all selected ads. " +
  "Identify patterns in creative format, hook types, tone, messaging strategy, " +
  "call-to-action approaches, and visual themes. Highlight what makes certain ads stand out " +
  "and flag any potential compliance concerns.";

function fmtTs(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function MdContent({ content }) {
  return (
    <div className="copilot-md">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}

function HBtn({ onClick, title, children, danger }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: danger ? "rgba(239,68,68,0.18)" : "rgba(255,255,255,0.15)",
        border: `1px solid ${danger ? "rgba(239,68,68,0.4)" : "rgba(255,255,255,0.3)"}`,
        borderRadius: 8, padding: "5px 10px", cursor: "pointer",
        fontSize: 13, color: "#fff", fontWeight: 600, lineHeight: 1,
      }}
    >
      {children}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AdsChatDrawer({
  open,
  onClose,
  ads,         // array of ad objects currently selected
  brandId,
  initialView, // "chat" | "history"
}) {
  const [view, setView] = useState(initialView || "chat");
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [model, setModel] = useState("gpt-4.1");
  const [progressMsg, setProgressMsg] = useState(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [confirmDestroyId, setConfirmDestroyId] = useState(null);

  const [autoPrompt, setAutoPrompt] = useState(undefined); // undefined=loading, string=ready
  const [autoPromptSource, setAutoPromptSource] = useState("default"); // "brand"|"global"|"default"
  const [sessionCost, setSessionCost] = useState(null); // null = no cost yet, number = USD total
  const [alertThreshold, setAlertThreshold] = useState(1.0); // USD, from settings
  const [pendingAlertAmount, setPendingAlertAmount] = useState(null); // milestone currently shown
  const shownAlerts = useRef(new Set()); // milestones already alerted this session

  const [exporting, setExporting] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const renameInputRef = useRef(null);
  const drawerRef = useRef(null);
  const resizing = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);
  const prevWidth = useRef(DEFAULT_WIDTH);
  const pendingAutoSend = useRef(false);

  // Build snapshot refs map from currently selected ads (no image fetch needed)
  // Backend reads PNGs directly from disk using creative_id.
  const snapshotRefs = useMemo(() => {
    const refs = {};
    for (const ad of (ads || [])) {
      const c = (ad.creatives || []).find(cr => cr.snapshot_cached_at);
      if (c) refs[String(ad.id)] = c.id;
    }
    return refs;
  }, [ads]);

  const hasSnapshots = Object.keys(snapshotRefs).length > 0;

  // ── Resize ──────────────────────────────────────────────────────────────────

  const onResizeStart = useCallback((e) => {
    if (isFullscreen) return;
    resizing.current = true;
    startX.current = e.clientX;
    startW.current = drawerRef.current?.offsetWidth || DEFAULT_WIDTH;
    e.preventDefault();
  }, [isFullscreen]);

  useEffect(() => {
    const onMove = (e) => {
      if (!resizing.current || !drawerRef.current) return;
      const dx = startX.current - e.clientX;
      const newW = Math.max(MIN_WIDTH, Math.min(window.innerWidth * 0.95, startW.current + dx));
      drawerRef.current.style.width = `${newW}px`;
      prevWidth.current = newW;
    };
    const onUp = () => { resizing.current = false; };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
  }, []);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); onClose?.(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // ── Session list ─────────────────────────────────────────────────────────────

  const loadSessionsList = useCallback(() => {
    if (!brandId) return;
    setSessionsLoading(true);
    adsListChatSessions(brandId)
      .then(data => setSessions(data?.items || []))
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  }, [brandId]);

  // Load list on open and when switching to history view
  useEffect(() => {
    if (open && brandId) loadSessionsList();
  }, [open, brandId, view]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset when drawer opens or ads selection changes ─────────────────────────

  useEffect(() => {
    if (!open) return;
    setView(initialView || "chat");
    setIsFullscreen(false);
    setRenamingId(null);
    setConfirmDestroyId(null);
    // Reset chat state when a new set of ads is selected
    setSessionId(null);
    setMessages([]);
    setInput("");
    setStreaming(false);
    setProgressMsg(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ads]);

  // Pre-load the configured auto prompt + cost alert threshold when the drawer opens.
  // Reads settings directly (same API the settings modal uses) — no middleman endpoint.
  // Precedence: brand-scope > global-scope > built-in default.
  useEffect(() => {
    if (!open) return;
    setAutoPrompt(undefined);
    setAutoPromptSource("default");

    // Priority: brand auto_message > global auto_message > brand chat_system > global chat_system > built-in default
    const AUTO_KEY   = "ads.prompt.chat_auto_message";
    const SYSTEM_KEY = "ads.prompt.chat_system";
    Promise.all([
      settingsList("global", null),
      brandId ? settingsList("brand", brandId) : Promise.resolve({ items: [] }),
    ])
      .then(([globalRes, brandRes]) => {
        const find = (items, key) =>
          (items || []).find(x => x?.key === key)?.value?.trim() || "";
        const bAuto   = find(brandRes?.items,  AUTO_KEY);
        const gAuto   = find(globalRes?.items, AUTO_KEY);
        const bSystem = find(brandRes?.items,  SYSTEM_KEY);
        const gSystem = find(globalRes?.items, SYSTEM_KEY);
        const resolved = bAuto || gAuto || bSystem || gSystem;
        const src = bAuto ? "brand" : gAuto ? "global" : bSystem ? "brand" : gSystem ? "global" : "default";
        setAutoPrompt(resolved || DEFAULT_AUTO_MESSAGE);
        setAutoPromptSource(resolved ? src : "default");

        // Cost alert threshold
        const rawThreshold = find(brandRes?.items, COST_ALERT_KEY) || find(globalRes?.items, COST_ALERT_KEY);
        setAlertThreshold(parseFloat(rawThreshold) || 1.0);
      })
      .catch(() => {
        setAutoPrompt(DEFAULT_AUTO_MESSAGE);
        setAutoPromptSource("default");
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brandId]);

  // Trigger cost alert when a new milestone is crossed
  useEffect(() => {
    if (sessionCost == null || pendingAlertAmount !== null) return;
    const milestones = getAlertMilestones(alertThreshold);
    const next = milestones.find(m => sessionCost >= m && !shownAlerts.current.has(m));
    if (next !== undefined) {
      shownAlerts.current.add(next);
      setPendingAlertAmount(next);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionCost, alertThreshold]);

  // Fire auto-send once state has fully reset AND auto prompt has loaded
  useEffect(() => {
    if (!pendingAutoSend.current) return;
    if (messages.length !== 0 || sessionId || streaming || adCount === 0) return;
    if (autoPrompt === undefined) return; // still fetching
    pendingAutoSend.current = false;
    sendMessage(autoPrompt);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, sessionId, streaming, autoPrompt]);

  // Scroll to bottom on new messages
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, progressMsg]);

  // Focus rename input
  useEffect(() => {
    if (renamingId) setTimeout(() => renameInputRef.current?.focus(), 50);
  }, [renamingId]);

  // ── Send message ─────────────────────────────────────────────────────────────

  const sendMessage = async (text) => {
    if (!text.trim() || streaming) return;
    setInput("");
    setStreaming(true);
    setProgressMsg(null);

    setMessages(prev => [...prev, { role: "user", content: text }]);
    setMessages(prev => [...prev, { role: "assistant", content: "", _streaming: true }]);

    let sid = sessionId;

    // Create session on first message if none exists
    if (!sid) {
      const adIds = (ads || []).map(a => a.id);
      if (!adIds.length) {
        setMessages(prev => {
          const next = [...prev];
          next[next.length - 1] = { role: "assistant", content: "No ads selected. Select ads in the matrix to start an analysis.", _error: true };
          return next;
        });
        setStreaming(false);
        return;
      }
      try {
        const data = await adsCreateChatSession(brandId, adIds, text.slice(0, 60));
        sid = data.session_id;
        setSessionId(sid);
      } catch (e) {
        setMessages(prev => {
          const next = [...prev];
          next[next.length - 1] = { role: "assistant", content: `Session error: ${e?.message || "unknown"}`, _error: true };
          return next;
        });
        setStreaming(false);
        return;
      }
    }

    adsChatSessionStream(
      {
        sessionId: sid,
        message: text,
        model,
        pinnedAdIds: null,
        snapshotRefs: hasSnapshots && VISION_MODELS.has(model) ? snapshotRefs : null,
      },
      // onChunk
      (chunk) => {
        setProgressMsg(null);
        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?._streaming) next[next.length - 1] = { ...last, content: last.content + chunk };
          return next;
        });
      },
      // onProgress
      (msg) => { setProgressMsg(msg); },
      // onDone
      () => {
        setProgressMsg(null);
        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?._streaming) next[next.length - 1] = { role: "assistant", content: last.content };
          return next;
        });
        setStreaming(false);
        loadSessionsList();
      },
      // onError
      (err) => {
        setProgressMsg(null);
        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?._streaming) next[next.length - 1] = { role: "assistant", content: `Error: ${err}`, _error: true };
          return next;
        });
        setStreaming(false);
      },
      // onCostUpdate
      (evt) => { setSessionCost(evt.session_total_cost_usd ?? null); },
    );
  };

  const handleExportMsg = async (msg, idx) => {
    if (exporting) return;
    setExporting(true);
    try { await exportMadPilotMessage({ sessionId, msg, msgIdx: idx }); }
    catch (e) { console.error("PPT export failed", e); }
    finally { setExporting(false); }
  };

  const handleExportSession = async () => {
    if (exporting || !messages.length) return;
    setExporting(true);
    const sessionName = sessions.find(s => s.session_id === sessionId)?.name;
    try { await exportMadPilotSession({ sessionId, messages, sessionName }); }
    catch (e) { console.error("PPT export failed", e); }
    finally { setExporting(false); }
  };

  const handleSend = () => sendMessage(input.trim());

  const handleAutoStart = () => {
    sendMessage(autoPrompt || DEFAULT_AUTO_MESSAGE);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleContinue = () => {
    // Check if more milestones are already crossed but not yet shown
    const milestones = getAlertMilestones(alertThreshold);
    const next = milestones.find(m => (sessionCost || 0) >= m && !shownAlerts.current.has(m));
    if (next !== undefined) {
      shownAlerts.current.add(next);
      setPendingAlertAmount(next);
    } else {
      setPendingAlertAmount(null);
    }
  };

  // ── Session management ────────────────────────────────────────────────────────

  const loadSession = async (session) => {
    try {
      const data = await adsGetChatSession(session.session_id);
      setSessionId(data.session_id);
      setMessages((data.messages || []).map(m => ({ role: m.role, content: m.content })));
      setSessionCost(data.total_cost_usd ?? null);
      shownAlerts.current = new Set();
      setPendingAlertAmount(null);
      setProgressMsg(null);
      setView("chat");
      setRenamingId(null);
      setConfirmDestroyId(null);
    } catch (e) {
      console.error("Failed to load session", e);
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
    setStreaming(false);
    setProgressMsg(null);
    setSessionCost(null);
    shownAlerts.current = new Set();
    setPendingAlertAmount(null);
    setRenamingId(null);
    setConfirmDestroyId(null);
    setView("chat");
  };

  const beginRename = (e, session) => {
    e.stopPropagation();
    setRenamingId(session.session_id);
    setRenameVal(session.name || "");
    setConfirmDestroyId(null);
  };

  const commitRename = async (sid) => {
    const trimmed = renameVal.trim();
    if (!trimmed) { setRenamingId(null); return; }
    try {
      await adsRenameChatSession(sid, trimmed);
      setSessions(prev => prev.map(s => s.session_id === sid ? { ...s, name: trimmed } : s));
    } catch {}
    setRenamingId(null);
  };

  const destroySession = async (e, sid) => {
    e.stopPropagation();
    if (confirmDestroyId === sid) {
      try {
        await adsDeleteChatSession(sid);
        setSessions(prev => prev.filter(s => s.session_id !== sid));
        if (sessionId === sid) startNewChat();
      } catch {}
      setConfirmDestroyId(null);
    } else {
      setConfirmDestroyId(sid);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      prevWidth.current = drawerRef.current?.offsetWidth || DEFAULT_WIDTH;
      setIsFullscreen(true);
    } else {
      setIsFullscreen(false);
      if (drawerRef.current) drawerRef.current.style.width = `${prevWidth.current}px`;
    }
  };

  if (!open) return null;

  const adCount = ads?.length || 0;
  const activeSession = sessions.find(s => s.session_id === sessionId);

  return (
    <>
    <style>{`@keyframes adsChatSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    <div
      className="copilot-backdrop"
      style={{ zIndex: 8000, ...(isFullscreen ? { display: "flex", justifyContent: "flex-end" } : {}) }}
    >
      {!isFullscreen && <div style={{ flex: 1 }} onClick={onClose} />}

      <div
        ref={drawerRef}
        className="copilot-drawer"
        style={isFullscreen ? { width: "100%", maxWidth: "100%", position: "relative" } : { position: "relative" }}
      >
        {!isFullscreen && <div className="copilot-resize-handle" onMouseDown={onResizeStart} />}

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="copilot-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            {view === "chat" && sessionId && renamingId === sessionId ? (
              <input
                ref={renameInputRef}
                value={renameVal}
                onChange={e => setRenameVal(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") commitRename(sessionId);
                  if (e.key === "Escape") setRenamingId(null);
                }}
                onBlur={() => commitRename(sessionId)}
                style={{
                  background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)",
                  borderRadius: 6, padding: "3px 8px", color: "#fff", fontSize: 14, fontWeight: 700,
                  outline: "none", width: "100%", fontFamily: "inherit",
                }}
              />
            ) : (
              <div
                className="copilot-header-title"
                onClick={() => {
                  if (view === "chat" && sessionId) {
                    setRenamingId(sessionId);
                    setRenameVal(activeSession?.name || "");
                  }
                }}
                title={view === "chat" && sessionId ? "Click to rename session" : undefined}
                style={{ cursor: view === "chat" && sessionId ? "text" : "default" }}
              >
                ✈ MAD Pilot
              </div>
            )}
            <div className="copilot-header-sub">
              {view === "history"
                ? `${sessions.length} session${sessions.length !== 1 ? "s" : ""}`
                : sessionId
                  ? <>
                      {activeSession?.name || "Session"}
                      {sessionCost != null && sessionCost > 0 && (
                        <span style={{ marginLeft: 6, fontWeight: 800, color: sessionCost >= alertThreshold ? "#fca5a5" : undefined }}>
                          ${sessionCost.toFixed(4)}
                        </span>
                      )}
                    </>
                  : adCount > 0
                    ? `${adCount} ad${adCount !== 1 ? "s" : ""} selected · New session`
                    : "Select ads to start"}
            </div>
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
            {view === "chat" && (
              <select
                value={model}
                onChange={e => setModel(e.target.value)}
                style={{
                  fontSize: 12, fontWeight: 700,
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: 7, padding: "5px 8px",
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff", cursor: "pointer", outline: "none",
                }}
              >
                {MODELS.map(m => <option key={m.id} value={m.id} style={{ background: "#1e1b4b", color: "#fff" }}>{m.label}</option>)}
              </select>
            )}

            {view === "chat" && messages.length > 0 && !streaming && (
              <HBtn onClick={handleExportSession} title={exporting ? "Exporting…" : "Export conversation to PPT"}>
                {exporting ? "…" : "↓ PPT"}
              </HBtn>
            )}

            {view === "chat" && sessionId && renamingId !== sessionId && (
              <HBtn onClick={() => { setRenamingId(sessionId); setRenameVal(activeSession?.name || ""); }} title="Rename session">✏</HBtn>
            )}

            {view === "chat" && sessionId && (
              <HBtn
                onClick={(e) => destroySession(e, sessionId)}
                title={confirmDestroyId === sessionId ? "Click again to confirm delete" : "Delete session"}
                danger={confirmDestroyId === sessionId}
              >
                {confirmDestroyId === sessionId ? "Confirm?" : "🗑"}
              </HBtn>
            )}

            <HBtn onClick={() => setView(v => v === "history" ? "chat" : "history")} title={view === "history" ? "Back to chat" : "Session history"}>
              {view === "history" ? "💬" : "🕐"}
            </HBtn>

            {view === "history" && adCount > 0 && (
              <>
                <HBtn onClick={() => { pendingAutoSend.current = true; startNewChat(); }} title="Auto-analyze selected ads">⚡ Auto</HBtn>
                <HBtn onClick={startNewChat} title="Start new chat manually">+ Manual</HBtn>
              </>
            )}
            {view === "history" && adCount === 0 && (
              <HBtn onClick={startNewChat} title="Start new chat">+ New</HBtn>
            )}

            <HBtn onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
              {isFullscreen ? "⤡" : "⤢"}
            </HBtn>

            <HBtn onClick={onClose} title="Close">✕</HBtn>
          </div>
        </div>

        {/* ── HISTORY VIEW ─────────────────────────────────────────────────── */}
        {view === "history" && (
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 6, background: "#f9fafb" }}>
            {sessionsLoading && (
              <div style={{ color: "#9ca3af", fontSize: 13, textAlign: "center", marginTop: 32 }}>Loading sessions…</div>
            )}
            {!sessionsLoading && sessions.length === 0 && (
              <div style={{ color: "#9ca3af", fontSize: 13, fontStyle: "italic", textAlign: "center", marginTop: 48 }}>
                No saved sessions for this brand yet.<br />
                <span style={{ fontSize: 12 }}>Select ads and start a chat to create one.</span>
              </div>
            )}
            {sessions.map(s => {
              const isActive = s.session_id === sessionId;
              const isConfirmDestroy = confirmDestroyId === s.session_id;
              const isRenaming = renamingId === s.session_id;
              return (
                <div
                  key={s.session_id}
                  onClick={() => !isRenaming && loadSession(s)}
                  style={{
                    border: `1px solid ${isActive ? "#c7d2fe" : "#e5e7eb"}`,
                    borderRadius: 10, padding: "10px 14px",
                    cursor: isRenaming ? "default" : "pointer",
                    background: isActive ? "#eef2ff" : "#fff",
                    display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8,
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e => { if (!isActive && !isRenaming) e.currentTarget.style.background = "#f5f5ff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isActive ? "#eef2ff" : "#fff"; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {isRenaming ? (
                      <input
                        ref={renameInputRef}
                        value={renameVal}
                        onChange={e => setRenameVal(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => {
                          if (e.key === "Enter") commitRename(s.session_id);
                          if (e.key === "Escape") setRenamingId(null);
                        }}
                        onBlur={() => commitRename(s.session_id)}
                        style={{ border: "1px solid #6366f1", borderRadius: 6, padding: "3px 8px", fontSize: 13, fontWeight: 700, width: "100%", outline: "none" }}
                      />
                    ) : (
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.name || "Untitled session"}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                      {fmtTs(s.updated_at)} · {s.message_count || 0} messages · {s.ad_count || 0} ads
                      {s.has_extraction && <span style={{ marginLeft: 6, color: "#6366f1", fontWeight: 700 }}>● extracted</span>}
                      {s.total_cost_usd != null && s.total_cost_usd > 0 && (
                        <span style={{ marginLeft: 6, color: "#059669", fontWeight: 700 }}>${s.total_cost_usd.toFixed(4)}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    <button onClick={e => beginRename(e, s)} title="Rename"
                      style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 6, padding: "3px 7px", cursor: "pointer", fontSize: 12, color: "#6b7280" }}>
                      ✏
                    </button>
                    <button onClick={e => destroySession(e, s.session_id)}
                      title={isConfirmDestroy ? "Click again to confirm" : "Delete session"}
                      style={{
                        background: isConfirmDestroy ? "#fee2e2" : "none",
                        border: `1px solid ${isConfirmDestroy ? "#fca5a5" : "#e5e7eb"}`,
                        borderRadius: 6, padding: "3px 7px", cursor: "pointer",
                        fontSize: 12, color: isConfirmDestroy ? "#dc2626" : "#6b7280",
                        fontWeight: isConfirmDestroy ? 800 : 400,
                      }}>
                      {isConfirmDestroy ? "Delete?" : "🗑"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── CHAT VIEW ────────────────────────────────────────────────────── */}
        {view === "chat" && (
          <>
            {/* Vision model warning */}
            {hasSnapshots && !VISION_MODELS.has(model) && (
              <div style={{
                background: "#fefce8", borderBottom: "1px solid #fde68a",
                padding: "8px 16px", fontSize: 12, fontWeight: 600, color: "#92400e",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                ⚠ This model doesn't support vision — screenshots won't be sent.
              </div>
            )}

            <div className="copilot-messages" style={{ background: "#f9fafb" }}>
              {messages.length === 0 && !progressMsg && (
                adCount > 0 && !sessionId ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, marginTop: 56, padding: "0 24px" }}>
                    <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 600, textAlign: "center" }}>
                      {adCount} ad{adCount !== 1 ? "s" : ""} selected — how would you like to start?
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
                      <button
                        onClick={handleAutoStart}
                        disabled={autoPrompt === undefined}
                        style={{
                          background: "#6366f1", color: "#fff", border: "none",
                          borderRadius: 10, padding: "10px 20px", fontWeight: 700,
                          fontSize: 13, cursor: autoPrompt === undefined ? "default" : "pointer",
                          display: "flex", alignItems: "center", gap: 6,
                          opacity: autoPrompt === undefined ? 0.6 : 1,
                        }}
                      >
                        ⚡ Auto Analysis
                      </button>
                      <button
                        onClick={() => textareaRef.current?.focus()}
                        style={{
                          background: "#fff", color: "#374151", border: "1.5px solid #e5e7eb",
                          borderRadius: 10, padding: "10px 20px", fontWeight: 700,
                          fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                        }}
                      >
                        ✏ Custom Question
                      </button>
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", textAlign: "center", maxWidth: 320 }}>
                      <strong>Auto</strong> runs a full analysis immediately. <strong>Custom</strong> lets you define exactly what to look for.
                      {autoPromptSource !== "default" && (
                        <div style={{ marginTop: 4, color: "#6366f1", fontWeight: 700 }}>
                          Using your {autoPromptSource} prompt from Settings.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ color: "#9ca3af", fontSize: 13, fontStyle: "italic", textAlign: "center", marginTop: 48 }}>
                    {sessionId
                      ? "Session loaded. Ask a follow-up question."
                      : "Resume a session from history, or select ads to start a new analysis."}
                  </div>
                )
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  style={{ display: "flex", flexDirection: "column", alignItems: msg.role === "user" ? "flex-end" : "flex-start", marginBottom: 12 }}
                >
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#9ca3af", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                    {msg.role === "user" ? "You" : "MAD Pilot"}
                  </div>
                  <div
                    className={`copilot-bubble ${msg.role === "user" ? "is-user" : msg._error ? "is-error" : "is-assistant"}`}
                    style={{
                      borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                      whiteSpace: msg.role === "user" ? "pre-wrap" : undefined,
                    }}
                  >
                    {msg.role === "user" ? (
                      msg.content
                    ) : msg._streaming && !msg.content ? (
                      progressMsg ? (
                        <span style={{ display: "flex", alignItems: "center", gap: 8, color: "#6b7280", fontSize: 13, padding: "2px 0" }}>
                          <span style={{
                            width: 14, height: 14, borderRadius: 999, flexShrink: 0,
                            border: "2px solid rgba(99,102,241,.3)", borderTopColor: "#6366f1",
                            animation: "adsChatSpin 0.9s linear infinite", display: "inline-block",
                          }} />
                          <span>{progressMsg}</span>
                        </span>
                      ) : (
                        <span style={{ display: "flex", alignItems: "center", gap: 8, color: "#6b7280", fontSize: 13, padding: "2px 0" }}>
                          <span>MAD Pilot is thinking…</span>
                          <span style={{
                            width: 14, height: 14, borderRadius: 999, flexShrink: 0,
                            border: "2px solid rgba(107,114,128,.3)", borderTopColor: "rgba(107,114,128,1)",
                            animation: "adsChatSpin 0.9s linear infinite", display: "inline-block",
                          }} />
                        </span>
                      )
                    ) : (
                      <MdContent content={msg.content} />
                    )}
                    {msg._streaming && msg.content && <span style={{ color: "#9ca3af" }}>▌</span>}
                  </div>
                  {!msg._streaming && msg.content && (
                    <button
                      onClick={() => handleExportMsg(msg, i)}
                      disabled={exporting}
                      title="Export this message to PPT"
                      style={{
                        marginTop: 4,
                        background: "none", border: "1px solid #e5e7eb",
                        borderRadius: 6, padding: "2px 8px",
                        fontSize: 11, fontWeight: 600, color: "#9ca3af",
                        cursor: exporting ? "default" : "pointer",
                        opacity: exporting ? 0.5 : 1,
                        transition: "color 0.12s, border-color 0.12s",
                      }}
                      onMouseEnter={e => { if (!exporting) { e.target.style.color = "#6366f1"; e.target.style.borderColor = "#6366f1"; } }}
                      onMouseLeave={e => { e.target.style.color = "#9ca3af"; e.target.style.borderColor = "#e5e7eb"; }}
                    >
                      ↓ PPT
                    </button>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="copilot-composer" style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  adCount > 0 || sessionId
                    ? "Ask MAD Pilot… (Enter to send, Shift+Enter for newline)"
                    : "Select ads or resume a session to start chatting…"
                }
                disabled={streaming || (adCount === 0 && !sessionId)}
                rows={2}
                style={{
                  flex: 1, resize: "none",
                  border: "1.5px solid #e5e7eb", borderRadius: 10,
                  padding: "9px 12px", fontSize: 13, fontWeight: 500,
                  color: "#111827", outline: "none", lineHeight: 1.5,
                  background: (streaming || (adCount === 0 && !sessionId)) ? "#f9fafb" : "#fff",
                  fontFamily: "inherit",
                }}
                onFocus={e => { e.target.style.borderColor = "#6366f1"; }}
                onBlur={e => { e.target.style.borderColor = "#e5e7eb"; }}
              />
              <button
                onClick={handleSend}
                disabled={streaming || !input.trim() || (adCount === 0 && !sessionId)}
                style={{
                  background: streaming || !input.trim() || (adCount === 0 && !sessionId) ? "#e5e7eb" : "#6366f1",
                  color: streaming || !input.trim() || (adCount === 0 && !sessionId) ? "#9ca3af" : "#fff",
                  border: "none", borderRadius: 10, padding: "10px 16px",
                  fontWeight: 700, fontSize: 13, cursor: streaming || !input.trim() ? "default" : "pointer",
                  transition: "background 0.15s",
                }}
              >
                {streaming ? "…" : "Send"}
              </button>
            </div>
          </>
        )}

        {/* ── Cost Alert Popup ─────────────────────────────────────────────── */}
        {pendingAlertAmount !== null && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 200,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <div style={{
              background: "#fff", borderRadius: 16, padding: "28px 32px",
              maxWidth: 360, width: "88%", textAlign: "center",
              boxShadow: "0 12px 48px rgba(0,0,0,0.22)",
            }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>⚠️</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#dc2626", marginBottom: 8 }}>
                Session Cost Alert
              </div>
              <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, marginBottom: 22 }}>
                This session has reached{" "}
                <strong style={{ color: "#dc2626" }}>${pendingAlertAmount % 1 === 0 ? pendingAlertAmount.toFixed(0) : pendingAlertAmount.toFixed(2)}</strong>.
                <br />
                Click <strong>Continue</strong> to proceed.
              </div>
              <button
                onClick={handleContinue}
                style={{
                  background: "#6366f1", color: "#fff", border: "none",
                  borderRadius: 9, padding: "11px 32px",
                  fontSize: 14, fontWeight: 800, cursor: "pointer",
                  transition: "background 0.15s",
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
